import * as React from 'react';
import { useState, useRef, useEffect } from 'react';
import { Box, Textarea, spacing } from '@apia/theme';
import { useFocus } from '../../../../hooks';
import IProperty from '../../../../JSApi/constants/IProperty';
import { IApiaTextArea } from '../../../../types';
import { getFieldErrorStyles } from '../style';
import MakeField from '../fieldsStore/MakeField';
import { useFieldContext } from '../fieldsStore/FieldContext';
import { useApiaComponentContext } from '../apiaComponent/ApiaComponentContext';
import useApiaFieldCommonProps from './useApiaFieldCommonProps';
import usePropsSelector from '../../../../store/propsStore/usePropsSelector';
import { formatMessage, noNaN } from '../../../../utils/util';
import { useApiaFormContext } from '../page/ApiaForm';

const ApiaTextArea = MakeField(
  React.forwardRef<HTMLTextAreaElement>((_, ref) => {
    const {
      state: { isValid, value },
      setValue,
      fireEvent,
    } = useFieldContext();
    const { field, fieldId } = useApiaComponentContext<IApiaTextArea>();
    const props = usePropsSelector(fieldId);
    const { attId, attName, fieldType, length, properties, attLabel } = field;
    const { name } = properties;

    const textComponentRef = useRef<HTMLTextAreaElement>(null);
    useFocus(textComponentRef);

    const [charsLeft, setCharsLeft] = useState<number>(-1);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setValue(e.target.value);
    };

    const handleBlur = () => {
      fireEvent('onChange');
    };

    useEffect(() => {
      if (typeof value === 'string') {
        const currLen = noNaN(value.length, -1);
        const percent = currLen / (length || currLen);
        if (percent > 0.75) setCharsLeft((length || 0) - currLen);
        else setCharsLeft(-1);
      }
    }, [length, value]);

    const { identifier, isReadonly } = useApiaFieldCommonProps({
      className: `textarea__field`,
    });

    const { isFormReadOnly } = useApiaFormContext();

    if (!(isFormReadOnly || props[IProperty.PROPERTY_INPUT_AS_TEXT])) {
      return (
        <Box className="textarea__field__container">
          <Textarea
            {...identifier}
            sx={{
              color: props[IProperty.PROPERTY_VALUE_COLOR]
                ? `${
                    props[IProperty.PROPERTY_VALUE_COLOR] as string
                  } !important`
                : '',
              width: props[IProperty.PROPERTY_SIZE]
                ? `${props[IProperty.PROPERTY_SIZE] as string}px`
                : '100%',
              ...getFieldErrorStyles(isValid),
            }}
            title={
              props[IProperty.PROPERTY_TITLE] ||
              props[IProperty.PROPERTY_TOOLTIP] ||
              attLabel
            }
            placeholder={props[IProperty.PROPERTY_PLACE_HOLDER]}
            autoFocus={
              props[IProperty.PROPERTY_AUTO_FOCUS] === 'true' ? true : undefined
            }
            autoComplete={props[IProperty.PROPERTY_AUTO_COMPLETE]}
            ref={ref}
            name={name || attName}
            aria-labelledby={`label_${fieldId}`}
            aria-label={props[IProperty.PROPERTY_ARIA_LABEL]}
            aria-description={props[IProperty.PROPERTY_ARIA_DESCRIPTION]}
            maxLength={length}
            value={value as string}
            required={props[IProperty.PROPERTY_REQUIRED]}
            disabled={props[IProperty.PROPERTY_DISABLED]}
            readOnly={isReadonly}
            onBlur={handleBlur}
            onChange={handleChange}
          />
          {length && length > 0 && (
            <Box
              sx={{
                mt: spacing(1),
                mb: spacing(0),
                color: '#666',
                display: 'flex',
                width: props[IProperty.PROPERTY_SIZE]
                  ? `${props[IProperty.PROPERTY_SIZE] as string}px`
                  : '100%',
                alignItems: 'baseline',
                justifyContent: 'space-between',
              }}
            >
              {charsLeft >= 0 &&
                formatMessage(window.LBL_CHARS_LEFT, {
                  TOK1: String(charsLeft),
                })}
            </Box>
          )}
        </Box>
      );
    }
    return (
      <Box
        {...identifier}
        as="span"
        id={fieldId}
        data-as-text="true"
        data-att-id={attId}
        data-type={fieldType}
        data-field-in-grid={props.attributes?.onGrid}
        data-name={name || attName}
      >
        {value as string}
      </Box>
    );
  }),
  {
    preSynchronize: ({
      fieldContext: {
        state: { value, lastValidValue },
      },
    }) => {
      return value === lastValidValue ? 'cancel' : true;
    },
    doSynchronize(
      {
        processFieldSubmit,
        fieldContext: {
          state: { value },
        },
      },
      additionalProps,
    ) {
      return processFieldSubmit({
        value,
        clearValues: true,
        ...additionalProps,
      });
    },
  },
);

export default ApiaTextArea;
